import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const navItems = ['About', 'Courses', 'Gallery', 'Instructors', 'Contact']

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false)
    if (location.pathname !== '/') {
      navigate('/')
      setTimeout(() => {
        document.getElementById(id.toLowerCase())?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } else {
      document.getElementById(id.toLowerCase())?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 mix-blend-difference dark:mix-blend-normal dark:bg-black/50 dark:backdrop-blur-sm text-white transition-all duration-300"
      >
        <div className="px-8 md:px-12 lg:px-20 py-8 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="text-2xl font-black tracking-tighter">
            SUDHA'S ACADEMY
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-12">
            {navItems.map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className="text-sm font-mono uppercase tracking-widest hover:underline underline-offset-4 transition-all"
              >
                {item}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/user/login"
              className="text-sm font-mono uppercase tracking-widest hover:underline underline-offset-4"
            >
              Login
            </Link>
            <Link
              to="/user/register"
              className="px-6 py-2 border border-white text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors"
            >
              Enroll
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black text-white md:hidden"
          >
            <div className="flex flex-col justify-center items-center h-full gap-8">
              {navItems.map((item, i) => (
                <motion.button
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => scrollToSection(item)}
                  className="text-3xl font-black tracking-tighter hover:text-gray-400 transition-colors"
                >
                  {item}
                </motion.button>
              ))}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex gap-6 mt-8"
              >
                <Link
                  to="/user/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-mono uppercase tracking-widest"
                >
                  Login
                </Link>
                <Link
                  to="/user/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-6 py-3 bg-white text-black text-sm font-bold uppercase tracking-widest"
                >
                  Enroll
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
